// End-of-sprint summary — the exec landing tab. A standardized scorecard (AI
// usage / efficiency / capacity / throughput / quality + work-mix trends) with
// prev-sprint deltas, plus a Claude-written interpretation. Data:
// GET /api/pulse/sprint-summary?sprintNum=N; POST .../generate to (re)write the
// narrative. sprintNum comes from the host (the dashboard's selected sprint).

import { useCallback, useEffect, useState } from 'react'
import { PULSE_COLORS } from './pulseConfig.js'

// Each metric's display + which direction is favourable for delta coloring.
const GROUPS = [
    {
        key: 'aiUsage',
        title: 'AI Usage',
        metrics: [
            { k: 'adoptionPct', label: 'AI adoption', unit: '%', better: 'higher' },
            { k: 'aiPoints', label: 'AI points', unit: '', better: 'higher' },
        ],
    },
    {
        key: 'efficiency',
        title: 'Efficiency — AI vs traditional',
        metrics: [
            { k: 'cycleTimeAi', label: 'Cycle · AI', unit: 'd', better: 'lower' },
            { k: 'cycleTimeNonAi', label: 'Cycle · trad', unit: 'd', better: 'lower' },
            { k: 'pointsPerDevAi', label: 'Pts/dev · AI', unit: '', better: 'higher' },
            { k: 'pointsPerDevNonAi', label: 'Pts/dev · trad', unit: '', better: 'higher' },
        ],
    },
    {
        key: 'capacity',
        title: 'Capacity',
        metrics: [
            { k: 'activeDevs', label: 'Active devs', unit: '', better: 'neutral' },
            { k: 'avgAvailabilityPct', label: 'Availability', unit: '%', better: 'higher' },
            { k: 'featurePct', label: 'Feature mix', unit: '%', better: 'neutral' },
            { k: 'ktloPct', label: 'KTLO mix', unit: '%', better: 'lower' },
        ],
    },
    {
        key: 'throughput',
        title: 'Throughput',
        metrics: [
            { k: 'velocity', label: 'Velocity', unit: ' pts', better: 'higher' },
            { k: 'storiesCompleted', label: 'Stories', unit: '', better: 'higher' },
            { k: 'rollovers', label: 'Rollovers', unit: '', better: 'lower' },
        ],
    },
    {
        key: 'quality',
        title: 'Quality',
        metrics: [
            { k: 'prodEscapes', label: 'Prod escapes', unit: '', better: 'lower' },
            { k: 'escapeRatePer100', label: 'Escape /100pts', unit: '', better: 'lower' },
            { k: 'changeFailurePct', label: 'Chg-fail (proxy)', unit: '%', better: 'lower' },
            { k: 'mttrDays', label: 'MTTR', unit: 'd', better: 'lower' },
        ],
    },
]

function MetricTile({ label, m, unit, better }) {
    const value = m?.value
    const delta = m?.delta
    let deltaColor = PULSE_COLORS.textSubtle
    if (delta != null && delta !== 0 && better !== 'neutral') {
        const good = better === 'lower' ? delta < 0 : delta > 0
        deltaColor = good ? PULSE_COLORS.success : PULSE_COLORS.danger
    }
    const arrow = delta == null || delta === 0 ? '' : delta < 0 ? '↓' : '↑'
    return (
        <div
            style={{
                background: PULSE_COLORS.bgInner,
                border: `1px solid ${PULSE_COLORS.border}`,
                borderRadius: '8px',
                padding: '12px',
                minWidth: '120px',
                flex: '1 1 120px',
            }}
        >
            <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', marginBottom: '4px' }}>{label}</div>
            <div style={{ color: PULSE_COLORS.text, fontSize: '22px', fontWeight: 700 }}>
                {value != null ? value : '—'}
                {value != null && <span style={{ fontSize: '12px', fontWeight: 500 }}>{unit}</span>}
            </div>
            {delta != null && delta !== 0 && (
                <div style={{ color: deltaColor, fontSize: '11px', fontWeight: 600 }}>
                    {arrow} {Math.abs(delta)}
                    {unit}
                </div>
            )}
        </div>
    )
}

function NarrativeList({ title, items, color }) {
    if (!items?.length) return null
    return (
        <div style={{ marginTop: '12px' }}>
            <div style={{ color, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {title}
            </div>
            <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: PULSE_COLORS.text, fontSize: '13px', lineHeight: 1.6 }}>
                {items.map((t, i) => (
                    <li key={i}>{t}</li>
                ))}
            </ul>
        </div>
    )
}

export default function SprintSummary({ sprintNum, refreshSignal }) {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [error, setError] = useState(null)

    const load = useCallback(async () => {
        if (!sprintNum) return
        setLoading(true)
        setError(null)
        try {
            const r = await fetch(`/api/pulse/sprint-summary?sprintNum=${sprintNum}`)
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? j?.message ?? `sprint-summary ${r.status}`)
            setData(j)
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }, [sprintNum])

    useEffect(() => {
        load()
    }, [load, refreshSignal])

    const generate = useCallback(async () => {
        if (!sprintNum) return
        setGenerating(true)
        setError(null)
        try {
            const r = await fetch(`/api/pulse/sprint-summary/generate?sprintNum=${sprintNum}&force=1`, { method: 'POST' })
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? j?.message ?? `generate ${r.status}`)
            setData({ ...j, model: j.model })
        } catch (e) {
            setError(e.message)
        } finally {
            setGenerating(false)
        }
    }, [sprintNum])

    const metrics = data?.metrics
    const narrative = data?.narrative
    const q = metrics?.quality

    return (
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div
                style={{
                    background: PULSE_COLORS.cardBg,
                    borderRadius: '12px',
                    padding: '24px',
                    border: `1px solid ${PULSE_COLORS.border}`,
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <div>
                        <h2 style={{ color: PULSE_COLORS.text, fontSize: '20px', fontWeight: 700, margin: 0 }}>
                            🧭 Sprint {sprintNum ?? '—'} Summary
                        </h2>
                        <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '6px 0 0' }}>
                            Standardized scorecard with prev-sprint deltas, interpreted by AI.
                        </p>
                    </div>
                    <button
                        onClick={generate}
                        disabled={generating || !sprintNum}
                        style={{
                            background: PULSE_COLORS.primary,
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 14px',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: generating ? 'default' : 'pointer',
                            opacity: generating ? 0.6 : 1,
                        }}
                    >
                        {generating ? 'Generating…' : narrative ? '↻ Regenerate' : '✨ Generate summary'}
                    </button>
                </div>

                {loading && <div style={{ color: PULSE_COLORS.textMuted, fontSize: '13px' }}>Loading…</div>}
                {error && <div style={{ color: PULSE_COLORS.danger, fontSize: '13px', marginTop: '8px' }}>⚠ {error}</div>}

                {/* Scorecard */}
                {metrics && (
                    <div style={{ marginTop: '16px' }}>
                        {GROUPS.map((g) => (
                            <div key={g.key} style={{ marginBottom: '16px' }}>
                                <div
                                    style={{
                                        color: PULSE_COLORS.textMuted,
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                        marginBottom: '8px',
                                    }}
                                >
                                    {g.title}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {g.metrics.map((mm) => (
                                        <MetricTile
                                            key={mm.k}
                                            label={mm.label}
                                            m={metrics[g.key]?.[mm.k]}
                                            unit={mm.unit}
                                            better={mm.better}
                                        />
                                    ))}
                                    {g.key === 'quality' && (
                                        <div
                                            style={{
                                                background: PULSE_COLORS.bgInner,
                                                border: `1px solid ${PULSE_COLORS.border}`,
                                                borderRadius: '8px',
                                                padding: '12px',
                                                minWidth: '180px',
                                                flex: '1 1 180px',
                                            }}
                                        >
                                            <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', marginBottom: '4px' }}>
                                                Defect injection /100 (180d)
                                            </div>
                                            <div style={{ fontSize: '15px', fontWeight: 700 }}>
                                                <span style={{ color: '#a78bfa' }}>AI {q?.defectInjectionAiPer100 ?? '—'}</span>
                                                <span style={{ color: PULSE_COLORS.textSubtle }}> · </span>
                                                <span style={{ color: '#38bdf8' }}>
                                                    Trad {q?.defectInjectionNonAiPer100 ?? '—'}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Work-mix trend strip */}
                        <div style={{ marginBottom: '8px' }}>
                            <div
                                style={{
                                    color: PULSE_COLORS.textMuted,
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    marginBottom: '8px',
                                }}
                            >
                                Work-mix trend
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <MetricTile label="Feature pts" m={metrics.workMix?.featurePts} unit="" better="neutral" />
                                <MetricTile label="Tech-debt pts" m={metrics.workMix?.techDebtPts} unit="" better="neutral" />
                                <MetricTile label="Bugs worked" m={metrics.workMix?.bugCount} unit="" better="lower" />
                                <MetricTile label="Support issues" m={metrics.workMix?.supportCount} unit="" better="lower" />
                            </div>
                        </div>
                    </div>
                )}

                {/* AI narrative */}
                {narrative ? (
                    <div
                        style={{
                            marginTop: '20px',
                            paddingTop: '20px',
                            borderTop: `1px solid ${PULSE_COLORS.border}`,
                        }}
                    >
                        <div style={{ color: PULSE_COLORS.text, fontSize: '15px', fontWeight: 700, lineHeight: 1.5 }}>
                            {narrative.headline}
                        </div>
                        <NarrativeList title="Trends" items={narrative.trends} color={PULSE_COLORS.primary} />
                        <NarrativeList title="Risks" items={narrative.risks} color={PULSE_COLORS.danger} />
                        <NarrativeList title="Wins" items={narrative.wins} color={PULSE_COLORS.success} />
                        {data?.model && (
                            <div style={{ color: PULSE_COLORS.textSubtle, fontSize: '11px', marginTop: '14px' }}>
                                Generated by {data.model}
                                {data.cached ? ' (cached)' : ''}
                            </div>
                        )}
                    </div>
                ) : (
                    metrics &&
                    !loading && (
                        <div
                            style={{
                                marginTop: '20px',
                                paddingTop: '20px',
                                borderTop: `1px solid ${PULSE_COLORS.border}`,
                                color: PULSE_COLORS.textMuted,
                                fontSize: '13px',
                            }}
                        >
                            No AI interpretation yet — click <strong>Generate summary</strong> to write one from the scorecard
                            above.
                        </div>
                    )
                )}
            </div>
        </div>
    )
}
